import classnames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Badge, Collapse, Table } from "reactstrap";
import {
  collectObsoleteBomLines,
  getObsoleteLineKey,
  OBSOLETE_ALT_SELECTED_ROW_CLASS,
  alternateRecommendationText,
  alternateRecommendationBadgeColor,
} from "../../../constants/digiKeyColumns";
import { ModalBox, ModalOverlay } from "../../Modals/ModalStyled";
import { Button } from "../../Styled";
import { DIGIKEY_VIEW_OPTIONS } from "../../../constants/urls";

const formatPrice = (alternate) => {
  if (alternate.unit_price_display) {
    return alternate.unit_price_display;
  }
  if (alternate.unit_price != null) {
    return `$${Number(alternate.unit_price).toFixed(5)}`;
  }
  return "N/A";
};

const substitutesFromLine = (line) =>
  Array.isArray(line?.substitutes) ? line.substitutes : [];

const ExternalLinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "inline-block", verticalAlign: "middle" }}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const substituteMpn = (alternate) =>
  String(
    alternate?.manufacturer_part_number ||
      alternate?.manufacturerPartNumber ||
      "",
  ).trim();

const AlternateRecommendationBadge = ({ label, text }) => {
  const display = String(text || alternateRecommendationText(label) || "").trim();
  if (!display) {
    return null;
  }

  return (
    <Badge
      color={alternateRecommendationBadgeColor(label)}
      className={`obsolete-rec-badge obsolete-rec-badge--${label || "none"}`}
    >
      {display}
    </Badge>
  );
};

const sortByUnitPrice = (items) =>
  [...items].sort((a, b) => {
    const priceA = a.unit_price != null ? Number(a.unit_price) : Infinity;
    const priceB = b.unit_price != null ? Number(b.unit_price) : Infinity;
    return priceA - priceB;
  });


const ObsoleteAlternateTable = ({
  substitutes,
  selectedMpn,
  onSelect,
  radioName,
}) => {
  const items = sortByUnitPrice(substitutes || []);

  if (!items.length) {
    return <p className="text-muted mb-0">No DigiKey alternates were found.</p>;
  }

  return (
    <div className="obsolete-alt-table-scroll">
      <Table className="table-bordered table-sm mb-0 parts-review-table obsolete-alt-table">
        <thead>
          <tr>
            <th style={{ width: "32px" }} />
            <th style={{ width: "9%" }}>Type</th>
            <th style={{ width: "28%" }}>MPN</th>
            <th>Manufacturer</th>
            <th>DigiKey PN</th>
            <th style={{ width: "12%" }}>Unit Price</th>
            <th style={{ width: "14%" }}>Package</th>
          </tr>
        </thead>
        <tbody>
          {items.map((alternate, index) => {
            const mpn = substituteMpn(alternate);
            const isSelected = selectedMpn === mpn;

            return (
              <tr
                key={`${mpn}-${index}`}
                className={classnames({
                  [OBSOLETE_ALT_SELECTED_ROW_CLASS]: isSelected,
                })}
                data-selected-alternate={isSelected ? "true" : undefined}
                onClick={() => onSelect(mpn)}
                style={{ cursor: "pointer" }}
              >
                <td>
                  <input
                    type="radio"
                    name={radioName}
                    checked={isSelected}
                    onMouseDown={(e) => e.preventDefault()}
                    onChange={() => onSelect(mpn)}
                  />
                </td>
                <td>{alternate.substitute_type || "N/A"}</td>
                <td className="obsolete-alt-mpn-td">
                  {mpn}
                  <AlternateRecommendationBadge
                    label={alternate.recommendation_label}
                    text={alternate.recommendation_text}
                  />
                </td>
                <td>{alternate.manufacturer || "N/A"}</td>
                <td>
                  {alternate.digikey_part_number || "N/A"}
                  {alternate.part_detail_url ? (
                    <a
                      href={alternate.part_detail_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View on DigiKey"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        marginLeft: "6px",
                        color: "#cc0000",
                        opacity: 0.75,
                        lineHeight: 1,
                      }}
                    >
                      <ExternalLinkIcon />
                    </a>
                  ) : null}
                </td>
                <td>{formatPrice(alternate)}</td>
                <td>{alternate.package || "N/A"}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

/**
 * Uses substitutes already attached to bom lines from digikey_enrichment.
 * Does not call DigiKey / Oxylabs again when opening the modal.
 */
const ObsoletePartsReviewModal = ({
  show,
  bomlines,
  focusLineKey,
  token,
  onSave,
  onClose,
  setLoading,
  setLoadingMessage,
  setLoadingPercent,
}) => {
  const obsoleteLines = useMemo(
    () => collectObsoleteBomLines(bomlines || []),
    [bomlines],
  );

  // Split into lines with alternatives vs lines with none found
  const { withAlternates, noAlternates } = useMemo(() => {
    const withAlts = [];
    const noAlts = [];
    obsoleteLines.forEach((line) => {
      if (substitutesFromLine(line).length > 0) {
        withAlts.push(line);
      } else {
        noAlts.push(line);
      }
    });
    return { withAlternates: withAlts, noAlternates: noAlts };
  }, [obsoleteLines]);

  const [expandedKey, setExpandedKey] = useState("");
  const [expandedNoAltKey, setExpandedNoAltKey] = useState("");
  const [selections, setSelections] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveError, setSaveError] = useState("");
  const simTimerRef = useRef(null);
  const accordionItemRefs = useRef({});
  const modalBoxRef = useRef(null);
  const initializedOpenRef = useRef(false);
  const didAutoScrollRef = useRef(false);
  const userHasScrolledRef = useRef(false);

  // Clear sim timer on unmount
  useEffect(() => () => { if (simTimerRef.current) clearInterval(simTimerRef.current); }, []);

  // Per-MPN state for DigiKey keyword suggestions
  const [suggestionsMap, setSuggestionsMap] = useState({});
  const [suggestionsLoadingMap, setSuggestionsLoadingMap] = useState({});
  const [suggestionsErrorMap, setSuggestionsErrorMap] = useState({});

  const fetchSuggestions = useCallback(async (mpn) => {
    if (!mpn || suggestionsMap[mpn] || suggestionsLoadingMap[mpn]) return;
    setSuggestionsLoadingMap((prev) => ({ ...prev, [mpn]: true }));
    setSuggestionsErrorMap((prev) => ({ ...prev, [mpn]: null }));
    try {
      const res = await fetch(
        `${DIGIKEY_VIEW_OPTIONS}?part_number=${encodeURIComponent(mpn)}`,
        {
          headers: {
            Accept: "application/json",
            authorization: `Bearer ${token}`,
          },
        },
      );
      const json = await res.json();
      if (!res.ok || !(json?.code >= 200 && json?.code < 400)) {
        throw new Error(json?.message || `Error ${res.status}`);
      }
      setSuggestionsMap((prev) => ({
        ...prev,
        [mpn]: json?.data?.options || json?.data?.substitutes || [],
      }));
    } catch (err) {
      setSuggestionsErrorMap((prev) => ({
        ...prev,
        [mpn]: err.message || "Failed to fetch suggestions",
      }));
    } finally {
      setSuggestionsLoadingMap((prev) => ({ ...prev, [mpn]: false }));
    }
  }, [suggestionsMap, suggestionsLoadingMap, token]);

  useEffect(() => {
    if (!show) {
      initializedOpenRef.current = false;
      didAutoScrollRef.current = false;
      userHasScrolledRef.current = false;
      return;
    }
    if (initializedOpenRef.current || !obsoleteLines.length) {
      return;
    }
    initializedOpenRef.current = true;

    const initialSelections = {};

    obsoleteLines.forEach((line) => {
      const key = getObsoleteLineKey(line);
      const substitutes = substitutesFromLine(line);
      const sessionSelected = String(line.selected_alternate_mpn || "").trim();
      if (sessionSelected) {
        initialSelections[key] = sessionSelected;
        return;
      }
      const recommendedMpn = String(line.preselect_alternate_mpn || "").trim();
      const recommendedInList = recommendedMpn
        ? substitutes.some(
            (item) =>
              substituteMpn(item).toUpperCase() === recommendedMpn.toUpperCase(),
          )
        : false;
      if (recommendedInList) {
        initialSelections[key] = substituteMpn(
          substitutes.find(
            (item) =>
              substituteMpn(item).toUpperCase() === recommendedMpn.toUpperCase(),
          ),
        );
        return;
      }
      const sorted = sortByUnitPrice(substitutes);
      if (sorted.length > 0) {
        const firstMpn = substituteMpn(sorted[0]);
        if (firstMpn) {
          initialSelections[key] = firstMpn;
        }
      }
    });

    setSelections(initialSelections);
    setSaveError("");
    setSuggestionsMap({});
    setSuggestionsLoadingMap({});
    setSuggestionsErrorMap({});

    const focusInNoAlt =
      !!focusLineKey &&
      noAlternates.some((line) => getObsoleteLineKey(line) === focusLineKey);
    if (focusInNoAlt) {
      setExpandedNoAltKey(focusLineKey);
      setExpandedKey("");
    } else {
      setExpandedKey(
        focusLineKey ||
          (withAlternates[0] ? getObsoleteLineKey(withAlternates[0]) : ""),
      );
      setExpandedNoAltKey("");
    }
  }, [show, obsoleteLines, focusLineKey, withAlternates, noAlternates]);

  // Scroll to a specific BOM line once when the modal opens. Do not repeat after
  // the user scrolls, expands another accordion, or picks a different alternate.
  useEffect(() => {
    if (!show || !focusLineKey || didAutoScrollRef.current) return;
    const isFocusedOpen =
      expandedKey === focusLineKey || expandedNoAltKey === focusLineKey;
    if (!isFocusedOpen) return;

    const firstWithAltKey = withAlternates[0]
      ? getObsoleteLineKey(withAlternates[0])
      : "";
    const firstNoAltKey = noAlternates[0]
      ? getObsoleteLineKey(noAlternates[0])
      : "";
    const isFirstVisibleItem =
      focusLineKey === firstWithAltKey ||
      (!firstWithAltKey && focusLineKey === firstNoAltKey);
    if (isFirstVisibleItem) {
      didAutoScrollRef.current = true;
      return;
    }

    const scrollFocusedItemExact = () => {
      if (userHasScrolledRef.current || didAutoScrollRef.current) return;
      const modal = modalBoxRef.current;
      const itemEl = accordionItemRefs.current[focusLineKey];
      if (!modal || !itemEl) return;

      const header = itemEl.querySelector(".obsolete-accordion-header");
      const modalRect = modal.getBoundingClientRect();
      const targetRect = (header || itemEl).getBoundingClientRect();
      const nextTop =
        modal.scrollTop + (targetRect.top - modalRect.top) - 12;

      modal.scrollTo({
        top: Math.max(0, nextTop),
        behavior: "auto",
      });
      didAutoScrollRef.current = true;
    };

    const t1 = setTimeout(scrollFocusedItemExact, 80);
    return () => {
      clearTimeout(t1);
    };
  }, [show, expandedKey, expandedNoAltKey, focusLineKey, withAlternates, noAlternates]);

  const toggleAccordion = (line) => {
    const key = getObsoleteLineKey(line);
    setExpandedKey((prev) => (prev === key ? "" : key));
  };

  const toggleNoAltAccordion = (line) => {
    const key = getObsoleteLineKey(line);
    setExpandedNoAltKey((prev) => (prev === key ? "" : key));
  };

  const findAlternateByMpn = (line, selectedMpn) => {
    const target = String(selectedMpn || "").trim().toUpperCase();
    if (!target) return null;
    return substitutesFromLine(line).find((item) => {
      const mpn = String(
        item.manufacturer_part_number || item.manufacturerPartNumber || "",
      )
        .trim()
        .toUpperCase();
      return mpn === target;
    }) || null;
  };

  const handleSave = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (saving) return;

    // Validate: every line that has alternatives must have a selection
    const unselected = withAlternates.filter((line) => {
      const key = getObsoleteLineKey(line);
      return !selections[key];
    });

    if (unselected.length > 0) {
      setSaveError(
        "Some alternatives are not selected. Please select all required alternatives before saving.",
      );
      setExpandedKey(getObsoleteLineKey(unselected[0]));
      return;
    }

    const payload = withAlternates
      .map((line) => {
        const key = getObsoleteLineKey(line);
        const selectedMpn = selections[key];
        if (!selectedMpn) return null;
        const alternate = findAlternateByMpn(line, selectedMpn);
        if (!alternate) return null;
        return { line, alternate };
      })
      .filter(Boolean);

    if (!payload.length) {
      setSaveError(
        "Could not resolve selected alternates. Please re-select each alternate and try again.",
      );
      return;
    }

    setSaveError("");
    const partCount = payload.length;

    const updateProgress = (pct, label) => {
      setSaveProgress(pct);
      if (typeof setLoadingPercent === "function") {
        setLoadingPercent(pct);
      }
      if (typeof setLoadingMessage === "function") {
        setLoadingMessage(label);
      }
    };

    // Force paint, then show the global OverlayLoader (z-index 9999) above the modal.
    flushSync(() => {
      setSaving(true);
      updateProgress(8, "Saving selections…");
      if (typeof setLoading === "function") {
        setLoading(true);
      }
    });

    let simPct = 8;
    simTimerRef.current = setInterval(() => {
      simPct = Math.min(simPct + 1.5, 94);
      const rounded = Math.round(simPct);
      let label = "Saving selections…";
      if (simPct >= 25 && simPct < 55) {
        label = `Fetching DigiKey data for ${partCount} selected part${partCount > 1 ? "s" : ""}…`;
      } else if (simPct >= 55 && simPct < 80) {
        label = "Enriching part details (datasheet, country of origin)…";
      } else if (simPct >= 80) {
        label = "Almost done, finishing up…";
      }
      updateProgress(rounded, label);
    }, 450);

    try {
      await onSave(payload);
      clearInterval(simTimerRef.current);
      simTimerRef.current = null;
      flushSync(() => {
        updateProgress(100, "Done!");
      });
      await new Promise((r) => setTimeout(r, 600));
      onClose();
    } catch (err) {
      clearInterval(simTimerRef.current);
      simTimerRef.current = null;
      setSaveError(
        err?.message || "Failed to save obsolete alternates. Please try again.",
      );
    } finally {
      setSaving(false);
      setSaveProgress(0);
      if (typeof setLoading === "function") {
        setLoading(false);
      }
      if (typeof setLoadingPercent === "function") {
        setLoadingPercent(0);
      }
      if (typeof setLoadingMessage === "function") {
        setLoadingMessage("");
      }
    }
  };

  if (!show || !obsoleteLines.length) {
    return null;
  }

  const selectedCount = withAlternates.filter((line) =>
    selections[getObsoleteLineKey(line)],
  ).length;

  return (
    <ModalOverlay className={classnames({ show })}>
      <ModalBox
        ref={modalBoxRef}
        className="theme-form obsolete-parts-review-modal"
        onScroll={() => {
          userHasScrolledRef.current = true;
        }}
        style={{
          maxWidth: "1600px",
          width: "calc(100vw - 48px)",
          maxHeight: "90vh",
          overflowX: "hidden",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <div className="obsolete-parts-modal-header">
          <h5 className="mb-0">Obsolete Parts – Select Alternates</h5>
          <button
            type="button"
            className="obsolete-parts-modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <p className="text-muted mb-3">
          {obsoleteLines.length} obsolete part(s) found. Expand each item to
          review substitutes and alternates, then save your selections.
        </p>

        {/* Parts with alternatives */}
        {withAlternates.length > 0 && (
          <div className="obsolete-parts-accordion mb-3">
            {withAlternates.map((line) => {
              const key = getObsoleteLineKey(line);
              const obsoleteMpn =
                line.obsolete_part_number || line.manufacturer_part_number;
              const substitutes = substitutesFromLine(line);
              const isOpen = expandedKey === key;
              const selectedMpn = selections[key] || "";

              const selectedAlternate = selectedMpn
                ? findAlternateByMpn(line, selectedMpn)
                : null;
            const selectedMfr = selectedAlternate?.manufacturer
              ? String(selectedAlternate.manufacturer).trim()
              : "";

            return (
                <div
                  key={key}
                  className={classnames("obsolete-accordion-item mb-2", {
                    "is-focused": focusLineKey === key,
                  })}
                  ref={(node) => {
                    if (node) {
                      accordionItemRefs.current[key] = node;
                    } else {
                      delete accordionItemRefs.current[key];
                    }
                  }}
                >
                  <button
                    type="button"
                    className={classnames(
                      "obsolete-accordion-header w-100 d-flex align-items-center justify-content-between",
                      { open: isOpen },
                    )}
                    onClick={() => toggleAccordion(line)}
                  >
                    <span>
                      <strong>{obsoleteMpn}</strong>
                      {line.manufacturer ? (
                        <span className="text-muted ml-2">
                          {line.manufacturer}
                        </span>
                      ) : null}
                    </span>
                    <span className="d-flex align-items-center">
                      {selectedMpn ? (
                        <Badge color="success" className="mr-2">
                          Selected: {selectedMpn}
                          {selectedMfr ? ` / ${selectedMfr}` : ""}
                        </Badge>
                      ) : null}
                      <Badge color="warning">{substitutes.length}</Badge>
                    </span>
                  </button>
                  <Collapse isOpen={isOpen}>
                    <div className="obsolete-accordion-body p-3">
                      <ObsoleteAlternateTable
                        substitutes={substitutes}
                        selectedMpn={selectedMpn}
                        radioName={`obsolete-alt-${key}`}
                        onSelect={(mpn) => {
                          setSaveError("");
                          setSelections((prev) => ({ ...prev, [key]: mpn }));
                        }}
                      />
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </div>
        )}

        {/* Parts with no alternatives found */}
        {noAlternates.length > 0 && (
          <div className="mb-3">
            <h6 className="text-muted mb-2" style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              No Alternates Found ({noAlternates.length})
            </h6>
            <div className="obsolete-parts-accordion">
              {noAlternates.map((line) => {
                const key = getObsoleteLineKey(line);
                const obsoleteMpn =
                  line.obsolete_part_number || line.manufacturer_part_number;
                const isOpen = expandedNoAltKey === key;
                const digiKeySearchUrl = `https://www.digikey.com/en/products/filter?keywords=${encodeURIComponent(obsoleteMpn || "")}`;

                return (
                  <div
                    key={key}
                    className={classnames("obsolete-accordion-item mb-2", {
                      "is-focused": focusLineKey === key,
                    })}
                    ref={(node) => {
                      if (node) {
                        accordionItemRefs.current[key] = node;
                      } else {
                        delete accordionItemRefs.current[key];
                      }
                    }}
                  >
                    <button
                      type="button"
                      className={classnames(
                        "obsolete-accordion-header w-100 d-flex align-items-center justify-content-between",
                        { open: isOpen },
                      )}
                      onClick={() => toggleNoAltAccordion(line)}
                    >
                      <span>
                        <strong>{obsoleteMpn}</strong>
                        {line.manufacturer ? (
                          <span className="text-muted ml-2">
                            {line.manufacturer}
                          </span>
                        ) : null}
                      </span>
                      <Badge color="secondary">0 Alternates</Badge>
                    </button>
                    <Collapse isOpen={isOpen}>
                      <div className="obsolete-accordion-body p-3">
                        {!suggestionsMap[obsoleteMpn] && !suggestionsLoadingMap[obsoleteMpn] && !suggestionsErrorMap[obsoleteMpn] && (
                          <div className="d-flex align-items-center">
                            <span className="text-muted mr-3" style={{ fontSize: "0.875rem" }}>
                              No DigiKey alternates were found for this part.
                            </span>
                            <Button
                              type="button"
                              className="importBtn blue px-3 w-auto"
                              style={{ fontSize: "0.8rem" }}
                              onClick={() => fetchSuggestions(obsoleteMpn)}
                            >
                              Show Similar Parts
                            </Button>
                          </div>
                        )}

                        {suggestionsLoadingMap[obsoleteMpn] && (
                          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                            Fetching similar parts…
                          </span>
                        )}

                        {suggestionsErrorMap[obsoleteMpn] && (
                          <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                            <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                              Could not load suggestions.
                            </span>
                            <a
                              href={digiKeySearchUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button
                                type="button"
                                className="importBtn blue px-3 w-auto"
                                style={{ fontSize: "0.8rem" }}
                              >
                                Search on DigiKey <ExternalLinkIcon />
                              </Button>
                            </a>
                            <Button
                              type="button"
                              className="importBtn px-3 w-auto"
                              style={{ fontSize: "0.8rem", background: "transparent", color: "#6c757d", border: "1px solid #ced4da" }}
                              onClick={() => {
                                setSuggestionsErrorMap((prev) => ({ ...prev, [obsoleteMpn]: null }));
                              }}
                            >
                              Retry
                            </Button>
                          </div>
                        )}

                        {suggestionsMap[obsoleteMpn] && (
                          <div>
                            {suggestionsMap[obsoleteMpn].length === 0 ? (
                              <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                                No similar parts found on DigiKey.
                              </span>
                            ) : (
                              <>
                                <p className="mb-2 text-muted" style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                  Similar Parts from DigiKey ({suggestionsMap[obsoleteMpn].length})
                                </p>
                                <div className="obsolete-alt-table-scroll">
                                  <table className="parts-review-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                                    <thead>
                                      <tr style={{ background: "#f1f3f5", borderBottom: "2px solid #dee2e6" }}>
                                        <th style={{ padding: "5px 8px", textAlign: "left", width: "18%" }}>MPN</th>
                                        <th style={{ padding: "5px 8px", textAlign: "left" }}>Description</th>
                                        <th style={{ padding: "5px 8px", textAlign: "left", width: "18%" }}>DigiKey PN</th>
                                        <th style={{ padding: "5px 8px", textAlign: "right", width: "12%" }}>Unit Price</th>
                                        <th style={{ padding: "5px 8px", textAlign: "right", width: "10%" }}>Stock</th>
                                        <th style={{ padding: "5px 8px", width: "72px" }} />
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {suggestionsMap[obsoleteMpn].map((opt, idx) => (
                                        <tr
                                          key={opt.digikey_part_number || opt.manufacturer_part_number || idx}
                                          style={{ borderBottom: "1px solid #dee2e6", background: idx % 2 === 0 ? "#fff" : "#f8f9fa" }}
                                        >
                                          <td style={{ padding: "5px 8px" }}>
                                            <strong>{opt.manufacturer_part_number}</strong>
                                            {opt.manufacturer ? (
                                              <div className="text-muted" style={{ fontSize: "0.75rem" }}>{opt.manufacturer}</div>
                                            ) : null}
                                          </td>
                                          <td style={{ padding: "5px 8px" }}>
                                            <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                              {opt.description || "—"}
                                            </span>
                                          </td>
                                          <td style={{ padding: "5px 8px", color: "#555" }}>
                                            {opt.digikey_part_number || "—"}
                                          </td>
                                          <td style={{ padding: "5px 8px", textAlign: "right" }}>
                                            {opt.unit_price_display || (opt.unit_price != null ? `$${Number(opt.unit_price).toFixed(4)}` : "—")}
                                          </td>
                                          <td style={{ padding: "5px 8px", textAlign: "right" }}>
                                            {opt.quantity_available != null ? String(opt.quantity_available) : "—"}
                                          </td>
                                          <td style={{ padding: "5px 8px", textAlign: "center" }}>
                                            {opt.part_detail_url ? (
                                              <a
                                                href={opt.part_detail_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ fontSize: "0.78rem" }}
                                              >
                                                View <ExternalLinkIcon />
                                              </a>
                                            ) : null}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                            <div className="mt-2">
                              <a
                                href={digiKeySearchUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: "0.8rem" }}
                              >
                                Search all on DigiKey <ExternalLinkIcon />
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </Collapse>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {saveError && (
          <div
            className="mb-3 px-3 py-2"
            style={{
              backgroundColor: "#fff3f3",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              color: "#842029",
              fontSize: "0.875rem",
            }}
          >
            {saveError}
          </div>
        )}

        <div className="obsolete-parts-modal-footer d-flex justify-content-between align-items-center">
          <span className="text-muted small">
            {selectedCount} of {withAlternates.length} part(s) selected
          </span>
          <div className="obsolete-parts-modal-actions d-flex align-items-center">
            <Button
              type="button"
              className="importBtn blue px-4 w-auto mr-2"
              disabled={saving}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="importBtn blue px-4 w-auto"
              disabled={saving || withAlternates.length === 0}
              onClick={handleSave}
            >
              {saving ? `Saving ${saveProgress}%` : "Save Selections"}
            </Button>
          </div>
        </div>
      </ModalBox>
    </ModalOverlay>
  );
};

export default ObsoletePartsReviewModal;
